import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Play, Square, ArrowLeft, Send, Sparkles, Volume2, VolumeX, ShieldAlert, Activity, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useApi, useQ, errMsg, fmtDuration, fmtTime } from "@/lib/api";
import { speak, stopSpeaking } from "@/lib/speech";
import { useT } from "@/lib/i18n";
import { Panel, LiveBadge, AgentAvatar } from "@/components/common";

const HEALTH = { healthy: "text-emerald-400", degraded: "text-amber-400", idle: "text-slate-500" };

const useElapsed = (stream) => {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    setSecs(stream?.elapsed_seconds || 0);
    if (stream?.status !== "live") return;
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [stream?.elapsed_seconds, stream?.status]);
  return secs;
};

const Stage = ({ stream, lastAi, speaking }) => {
  const { t } = useT();
  return (
    <div className="relative rounded-lg overflow-hidden border border-white/10 bg-[#0B0B10] aspect-video flex flex-col items-center justify-center grain" data-testid="stream-stage">
      <div className="absolute top-4 left-4"><LiveBadge live={stream.status === "live"} /></div>
      <AgentAvatar agent={stream.agent} size="h-28 w-28 sm:h-36 sm:w-36" speaking={speaking} />
      <p className="font-display text-2xl font-bold mt-5">{stream.agent?.name}</p>
      <p className="text-xs text-slate-500 uppercase tracking-[0.2em]">{t(`mode_${stream.mode}`)}</p>
      {lastAi && (
        <div className="absolute bottom-0 inset-x-0 bg-zinc-950/80 backdrop-blur-xl border-t border-white/10 p-4" data-testid="stage-subtitle">
          <p className="text-sm sm:text-base text-slate-100 line-clamp-3">{lastAi.text}</p>
        </div>
      )}
    </div>
  );
};

const SOURCE = { youtube: ["YouTube", "text-rose-300 bg-rose-500/10"], auto: ["AUTO", "text-emerald-300 bg-emerald-500/10"], simulator: ["SIM", "text-slate-400 bg-white/5"] };

const ChatMessage = ({ m }) => {
  const { t } = useT();
  const blocked = m.status === "blocked";
  const src = SOURCE[m.source];
  return (
    <div className={`px-3 py-2 rounded-md text-sm ${m.role === "ai" ? "bg-indigo-950/30 border-l-2 border-indigo-500" : blocked ? "bg-rose-950/20 opacity-70" : "hover:bg-white/[0.03]"}`} data-testid={`chat-msg-${m.id}`}>
      <div className="flex items-center gap-2 text-xs mb-0.5">
        <span className={`font-semibold ${m.role === "ai" ? "text-indigo-300" : "text-slate-300"}`}>{m.author}</span>
        {src && <span className={`rounded px-1.5 py-px text-[10px] font-semibold ${src[1]}`} data-testid={`chat-source-${m.id}`}>{src[0]}</span>}
        {m.posted_to_youtube && <Youtube className="h-3 w-3 text-rose-400" />}
        <span className="text-slate-600 font-mono-ui">{fmtTime(m.created_at)}</span>
        {blocked && <span className="inline-flex items-center gap-1 text-rose-400"><ShieldAlert className="h-3 w-3" />{t("blocked")}</span>}
      </div>
      <p className={`${blocked ? "line-through text-slate-500" : "text-slate-200"}`}>{m.text}</p>
    </div>
  );
};

const ViewerSimulator = ({ streamId, live, onSent }) => {
  const { t } = useT();
  const api = useApi();
  const [author, setAuthor] = useState("viewer_" + Math.floor(Math.random() * 900 + 100));
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/streams/${streamId}/viewer-message`, { author, text }); setText(""); onSent(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="border-t border-white/10 p-3 space-y-2" data-testid="viewer-simulator">
      <p className="eyebrow">{t("simulate_viewer")}</p>
      <div className="flex gap-2">
        <Input value={author} onChange={(e) => setAuthor(e.target.value)} className="w-32 shrink-0" data-testid="viewer-author-input" />
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={live ? t("viewer_ph") : t("go_live_first")} disabled={!live} data-testid="viewer-message-input" />
        <Button type="submit" disabled={!live || busy} className="bg-indigo-600 hover:bg-indigo-500" data-testid="viewer-send-btn"><Send className={`h-4 w-4 ${busy ? "animate-pulse" : ""}`} /></Button>
      </div>
    </form>
  );
};

const Controls = ({ stream, onChange }) => {
  const { t } = useT();
  const api = useApi();
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState("");
  const live = stream.status === "live";
  const run = async (key, fn) => { setBusy(key); try { await fn(); onChange(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };
  return (
    <Panel className="space-y-4" data-testid="stream-controls">
      <div className="flex gap-2">
        {live ? (
          <Button onClick={() => run("stop", () => api.post(`/streams/${stream.id}/stop`))} disabled={!!busy} className="flex-1 rounded-full bg-rose-600 hover:bg-rose-500" data-testid="stream-stop-btn"><Square className="h-4 w-4 mr-1" />{t("stop_stream")}</Button>
        ) : (
          <Button onClick={() => run("start", () => api.post(`/streams/${stream.id}/start`))} disabled={!!busy} className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-500" data-testid="stream-start-btn"><Play className="h-4 w-4 mr-1" />{t("go_live")}</Button>
        )}
      </div>
      <div className="space-y-2">
        <p className="eyebrow">{t("presenter_segment")}</p>
        <div className="flex gap-2">
          <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={stream.topic || t("topic_ph")} disabled={!live} data-testid="segment-topic-input" />
          <Button variant="outline" disabled={!live || !!busy} onClick={() => run("seg", () => api.post(`/streams/${stream.id}/segment`, { topic }))} data-testid="segment-generate-btn">
            <Sparkles className={`h-4 w-4 ${busy === "seg" ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>
    </Panel>
  );
};

const INTERVALS = [15, 30, 60, 120];

const AutopilotSettings = ({ stream, onChange }) => {
  const { t } = useT();
  const api = useApi();
  const save = async (patch) => { try { await api.patch(`/streams/${stream.id}/settings`, patch); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  const yt = stream.channel?.connection === "youtube" && stream.broadcast_id;
  return (
    <Panel className="space-y-4" data-testid="autopilot-settings">
      <p className="eyebrow">{t("autopilot")}</p>
      <div className="flex items-center justify-between gap-3">
        <div><p className="text-sm font-medium">{t("auto_viewers")}</p><p className="text-xs text-slate-500">{t("auto_viewers_hint")}</p></div>
        <Switch checked={!!stream.auto_viewers} onCheckedChange={(v) => save({ auto_viewers: v })} data-testid="room-auto-viewers-switch" />
      </div>
      {stream.auto_viewers && (
        <div className="flex flex-wrap gap-2" data-testid="auto-interval-group">
          {INTERVALS.map((s) => (
            <button key={s} onClick={() => save({ auto_viewers_interval: s })} data-testid={`auto-interval-${s}`}
              className={`rounded-full px-3 py-1 text-xs border transition-colors ${stream.auto_viewers_interval === s ? "bg-emerald-600/20 border-emerald-500/40 text-emerald-200" : "border-white/10 text-slate-400 hover:text-white"}`}>
              {t("every")} {s}s
            </button>
          ))}
        </div>
      )}
      {yt && (
        <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/10">
          <div>
            <p className="text-sm font-medium flex items-center gap-2"><Youtube className="h-4 w-4 text-rose-400" />{t("post_to_youtube")}</p>
            <p className="text-xs text-slate-500" data-testid="yt-chat-status">{stream.live_chat_id ? t("yt_chat_linked") : t("yt_chat_waiting")}</p>
          </div>
          <Switch checked={stream.post_to_youtube !== false} onCheckedChange={(v) => save({ post_to_youtube: v })} data-testid="room-post-youtube-switch" />
        </div>
      )}
      {(stream.scheduled_at || stream.ends_at) && (
        <p className="text-xs text-slate-400 pt-3 border-t border-white/10" data-testid="room-schedule-info">
          {stream.scheduled_at && <>{t("starts")} {new Date(stream.scheduled_at).toLocaleString()} </>}
          {stream.ends_at && <>· {t("ends")} {new Date(stream.ends_at).toLocaleString()}</>}
        </p>
      )}
    </Panel>
  );
};

export default function StreamRoom() {
  const { id } = useParams();
  const { t } = useT();
  const qc = useQueryClient();
  const { data: stream } = useQ(`/streams/${id}`, { refetchInterval: 8000 });
  const live = stream?.status === "live";
  const { data: msgs = [] } = useQ(`/streams/${id}/messages`, { refetchInterval: live ? 2500 : false });
  const [autoVoice, setAutoVoice] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const spoken = useRef(null);
  const listRef = useRef(null);
  const secs = useElapsed(stream);
  const aiMsgs = msgs.filter((m) => m.role === "ai");
  const lastAi = aiMsgs[aiMsgs.length - 1];

  const refresh = () => { qc.invalidateQueries({ queryKey: [`/streams/${id}`] }); qc.invalidateQueries({ queryKey: [`/streams/${id}/messages`] }); qc.invalidateQueries({ queryKey: ["/overview"] }); };

  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); }, [msgs.length]);
  useEffect(() => {
    if (!lastAi) return;
    if (spoken.current === null) { spoken.current = lastAi.id; return; }
    if (spoken.current === lastAi.id || !autoVoice || !stream?.agent) return;
    spoken.current = lastAi.id;
    const a = stream.agent;
    setSpeaking(true);
    speak(lastAi.text, { voice: a.voice, rate: a.voice_speed, pitch: a.voice_pitch, lang: a.language, onEnd: () => setSpeaking(false) });
  }, [lastAi, autoVoice, stream?.agent]);
  useEffect(() => () => stopSpeaking(), []);

  if (!stream) return <div className="h-64 rounded-lg bg-white/[0.03] animate-pulse" />;
  const received = msgs.filter((m) => m.role === "viewer").length;
  const answered = msgs.filter((m) => m.role === "viewer" && m.status === "answered").length;

  return (
    <div>
      <Link to="/dashboard/streams" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-4" data-testid="back-to-streams"><ArrowLeft className="h-4 w-4" />{t("nav_streams")}</Link>
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="stream-room-title">{stream.title}</h1>
        <span className="font-mono-ui text-lg text-slate-300" data-testid="stream-timer">{fmtDuration(secs)}</span>
        <span className={`inline-flex items-center gap-1 text-sm ${HEALTH[stream.health]}`} data-testid="stream-health"><Activity className="h-4 w-4" />{stream.status === "ended" ? t("status_ended") : t(`health_${stream.health}`)}</span>
      </div>
      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 space-y-4">
          <Stage stream={stream} lastAi={lastAi} speaking={speaking} />
          <div className="grid grid-cols-3 gap-4 text-center">
            {[[t("msgs_received"), received, "room-received"], [t("msgs_answered"), answered, "room-answered"], [t("ai_responses"), aiMsgs.length, "room-ai"]].map(([l, v, tid]) => (
              <Panel key={tid} className="!p-4" data-testid={tid}><p className="font-display text-2xl font-bold">{v}</p><p className="text-[11px] text-slate-500 uppercase tracking-wider">{l}</p></Panel>
            ))}
          </div>
          <Controls stream={stream} onChange={refresh} />
          <AutopilotSettings stream={stream} onChange={refresh} />
        </div>
        <div className="lg:col-span-2 rounded-lg border border-white/[0.08] bg-[#111118] flex flex-col h-[640px]" data-testid="live-chat-panel">
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <p className="font-display font-bold">{t("live_chat")}</p>
            <label className="flex items-center gap-2 text-xs text-slate-400">
              {autoVoice ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />} {t("ai_voice")}
              <Switch checked={autoVoice} onCheckedChange={(v) => { setAutoVoice(v); if (!v) { stopSpeaking(); setSpeaking(false); } }} data-testid="auto-voice-switch" />
            </label>
          </div>
          <div ref={listRef} className="flex-1 overflow-y-auto scrollbar-thin p-2 space-y-1" data-testid="chat-list">
            {msgs.length === 0 && <p className="text-sm text-slate-500 p-3">{t("chat_empty")}</p>}
            {msgs.map((m) => <ChatMessage key={m.id} m={m} />)}
          </div>
          <ViewerSimulator streamId={id} live={live} onSent={refresh} />
        </div>
      </div>
    </div>
  );
}
